import { useCallback, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { WASTE, EDITOR } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { GapBadge, ReportStatus } from "@/components/waste/GapBadge";
import { ReviewDialog } from "@/components/waste/ReviewDialog";
import { ReportEditor, toPayload, PATHWAYS, EVIDENCE_KINDS } from "@/components/waste/ReportEditor";
import { Plus, ClipboardList } from "lucide-react";
import { toast } from "sonner";

export function ReportList({ reports, onOpen, rowId = WASTE.reportRow }) {
  if (!reports.length) return <p className="text-sm text-muted-foreground">No reports.</p>;
  return (
    <div className="divide-y rounded-xl border border-sage-light bg-white">
      {reports.map((r) => (
        <button key={r.id} onClick={() => onOpen(r.id)} data-testid={rowId(r.id)} className="flex w-full flex-wrap items-center gap-2 px-3 py-2.5 text-left hover:bg-sage-surface">
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-forest-dark">{r.subcontractor_name}</span>
            <span className="block truncate text-xs text-muted-foreground">{r.contract_name} · {r.period}{r.entered_by_limelite ? " · entered by Limelite" : ""}</span>
          </span>
          <span className="text-xs text-muted-foreground">{r.gap?.metrics?.diversion_pct ?? 0}% diverted</span>
          <GapBadge status={r.gap?.status} />
          <ReportStatus status={r.status} />
        </button>
      ))}
    </div>
  );
}

function BehalfDialog({ open, onClose, subs, onCreated }) {
  const [subId, setSubId] = useState("");
  const [form, setForm] = useState({ contract_id: "", period: new Date().toISOString().slice(0, 7), lines: [], evidence: [], notes: "" });
  const sub = subs.find((s) => s.id === subId);
  const save = async () => {
    try {
      const r = await api.post("/sub-reports", { subcontractor_id: subId, contract_id: form.contract_id, period: form.period, ...toPayload(form) });
      toast.success("Draft saved — submit it from the report"); onCreated(r.data.id);
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl" data-testid={WASTE.behalfDialog}>
        <DialogHeader><DialogTitle className="font-heading">Enter a report on behalf of a subcontractor</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="space-y-1.5"><Label>Subcontractor</Label>
            <Select value={subId} onValueChange={(v) => { setSubId(v); setForm((f) => ({ ...f, contract_id: "" })); }}>
              <SelectTrigger data-testid={WASTE.behalfSub}><SelectValue placeholder="Choose" /></SelectTrigger>
              <SelectContent>{subs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Project</Label>
            <Select value={form.contract_id} onValueChange={(v) => setForm((f) => ({ ...f, contract_id: v }))} disabled={!sub}>
              <SelectTrigger data-testid={EDITOR.project}><SelectValue placeholder="Choose" /></SelectTrigger>
              <SelectContent>{(sub?.projects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Period</Label><Input type="month" value={form.period} onChange={(e) => setForm((f) => ({ ...f, period: e.target.value }))} data-testid={EDITOR.period} /></div>
        </div>
        <ReportEditor value={form} onChange={setForm} pathways={PATHWAYS} kinds={EVIDENCE_KINDS} />
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!subId || !form.contract_id || !form.period} className="bg-forest-mid hover:bg-forest-dark" data-testid={WASTE.behalfSave}>Save draft</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function InboxTab({ projects }) {
  const [status, setStatus] = useState("submitted");
  const [reports, setReports] = useState([]);
  const [subs, setSubs] = useState([]);
  const [open, setOpen] = useState(null);
  const [behalf, setBehalf] = useState(false);
  const load = useCallback(() => {
    api.get("/sub-reports", { params: status === "all" ? {} : { status } }).then((r) => setReports(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [status]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/subcontractors").then((r) => setSubs(r.data.map((s) => ({ ...s, projects: projects.filter((p) => (s.contract_ids || []).includes(p.contract_id)).map((p) => ({ id: p.contract_id, name: p.name })) }))));
  }, [projects]);
  return (
    <Card className="border-sage-light p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-forest-dark"><ClipboardList className="h-4 w-4 text-forest-mid" /> Subcontractor reports</h3>
        <div className="flex gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-9 w-[160px]" data-testid={WASTE.statusFilter}><SelectValue /></SelectTrigger>
            <SelectContent>{["submitted", "returned", "accepted", "draft", "all"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s === "submitted" ? "Awaiting review" : s}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" className="bg-forest-mid hover:bg-forest-dark" onClick={() => setBehalf(true)} data-testid={WASTE.newReportBtn}><Plus className="mr-1 h-4 w-4" /> Enter on behalf</Button>
        </div>
      </div>
      <ReportList reports={reports} onOpen={setOpen} />
      <ReviewDialog reportId={open} onClose={() => setOpen(null)} onChanged={load} />
      {behalf && <BehalfDialog open={behalf} subs={subs} onClose={() => setBehalf(false)} onCreated={(id) => { setBehalf(false); setStatus("draft"); setOpen(id); }} />}
    </Card>
  );
}
